import type { ReactNode } from "react";

import type { UiTheme } from "../theme";
import { Chip } from "./Chip";
import { ChoiceTray, type ChoiceTrayOption } from "./ChoiceTray";
import { useDisclosure } from "./useDisclosure";

// GymChip (EQ-1 slice D) — where you are training, as one chip that opens a
// tray in place.
//
// ⭐ What the place IS, in Antenore's words (2026-08-13): *"la palestra è solo
// un grouping per facilitare la pesca degli esercizi … essendo un grouping non
// impatta il logging, ma solo la scelta degli esercizi"*. So this control never
// changes what gets logged or how — it changes what surfaces first when you go
// looking for an exercise or a machine. Everything stays reachable.
//
// ⚠️ THREE mounts, all shipped (equipment-model.md §9.12.3) — the logger's
// session row, the add picker, the swap picker — so that none of them can
// answer "which gym" its own way. Antenore's constraint, not a preference:
// *"nel add e nel replace, quello deve imperativamente utilizzare la stessa
// primitiva"*. The two picker mounts landed with slice D2b; each host calls
// the hook ITSELF rather than sharing one result, because the disclosure below
// is per-call and shared nodes would open every tray at once.
//
// ⛔ Why a HOOK and not a component. The trigger belongs in a header row and
// the tray belongs full-width UNDER it. Returning one <View> that stacks them
// meant the tray inherited the width of whatever narrow flex cell the trigger
// sat in — in the roster header, a `flexShrink: 0` column a chip wide. The
// hook hands the host two nodes and lets it place each one properly, while the
// implementation of both stays here, once.

export type GymChipPlace = {
  /** The bare slug stored on the entry (`home`, `gym`, `dobedan`). */
  value: string;
  /** Already localised by the host — see mobile's locationLabel. */
  label: string;
};

export type GymChipProps = {
  theme: UiTheme;
  /** Where we are now, or null when the athlete has not said. */
  value: string | null;
  /** How to read `value`; also used for the tray's option labels. */
  places: readonly GymChipPlace[];
  onChoose: (value: string) => void;
  /** Selects the default (no named place). */
  onClear: () => void;
  /** Opens the host's "name a new place" flow (free text). */
  onCreate?: () => void;
  labels: {
    /** Chip text when no place is set. */
    idle: string;
    /** The tray's question, e.g. "Where are you training?". */
    question: string;
    create: string;
    /** First option: the unnamed default everyone starts on. */
    fallback: string;
    /** Spoken on the chip so a reader hears what the control is FOR. */
    a11y: string;
    /** One line under the tray: what choosing a place does, and does not. */
    footnote?: string;
  };
  /**
   * EQ-1-GYM-DEFAULT — the ★ on each place row: which place is the athlete's
   * DEFAULT gym, reusing the machine tray's per-row action (Antenore 2026-08-20,
   * *"lo stesso meccanismo a stellina del replace"*).
   *
   * ⛔ Offered on NAMED places only, unlike the machine tray's ★ which also sits
   * on the Default row. The store is a SCALAR (`Profile.defaultLocation`, a place
   * slug) with no room for "the unnamed place is my explicit default" as distinct
   * from "no default": a ★ on the Default row would toggle nothing the store can
   * hold, and its spoken label could not follow a state that never changes. The
   * three-state gesture the machine star has — elect / elect another / re-tap to
   * demote — is expressed by the named rows alone, and electing the unnamed place
   * is a no-op the absence already means.
   *
   * ⛔ Omitted entirely when the host has no door to persist through (tests, a
   * mount with no profile): absent prop = no ★ anywhere, and the tray renders
   * exactly as it did before — the same opt-in contract `ChoiceTrayOption.action`
   * gives the machine tray's other, star-less consumer.
   */
  pin?: {
    /** The elected default place slug, or undefined when none is. */
    pinned: string | undefined;
    /** Tap the ★ on a place row. Re-tapping the place already in force demotes
     *  it back to no default. */
    onToggle: (value: string) => void;
    /** a11y label for a row's ★, built by the host — the primitive holds no
     *  i18n. `active` says whether this place is the current default, so the
     *  label describes the STATE, never the bare control (a toggle that says the
     *  same words pressed and unpressed tells a screen-reader user nothing). */
    accessibilityLabel: (placeLabel: string, active: boolean) => string;
    /** The hint follows the state too, not just the label. */
    accessibilityHint: (active: boolean) => string;
  };
};

export function useGymChip({
  theme,
  value,
  places,
  onChoose,
  onClear,
  onCreate,
  labels,
  pin,
}: GymChipProps): { trigger: ReactNode; tray: ReactNode } {
  const disclosure = useDisclosure(false);
  const current = places.find((p) => p.value === value);

  // The default leads, then the places the athlete actually made.
  //
  // ⛔ No pre-named places (Antenore, 2026-08-13): the first version shipped
  // "Home" and "Gym" as ready-made options and he refused them — *"non voglio
  // posti già nominati, voglio Default e basta, poi Add place"*. A name the
  // app invented is a name nobody asked for, and it is the same objection that
  // killed invented machine names: you do not recognise a label you did not
  // choose. The only place that exists without being created is the unnamed
  // one, which is where everyone already is.
  //
  // ⛔ The unnamed place is <ChoiceTray>'s own `defaultOption` now, not an
  // empty-string key unpicked here (EQ-1-DEFAULT, 2026-08-14). The machine
  // tray needed the same absence-option and would have been the second copy of
  // the convention; modelled in the primitive there is no sentinel key left to
  // collide, mistype or store.
  const options: ChoiceTrayOption[] = places.map((p) => ({
    key: p.value,
    label: p.label,
    selected: p.value === value,
    // ⛔ The action follows the STATE (active + label + hint), mirroring the
    // machine tray's ★ one surface over — the primitive is the SAME
    // <ChoiceTrayOption.action> box, reserved in the flex row so a tap can never
    // be stolen by the neighbouring chip (DS-51).
    ...(pin
      ? {
          action: {
            active: pin.pinned === p.value,
            accessibilityLabel: pin.accessibilityLabel(
              p.label,
              pin.pinned === p.value,
            ),
            accessibilityHint: pin.accessibilityHint(pin.pinned === p.value),
            onPress: () => pin.onToggle(p.value),
          },
        }
      : {}),
  }));

  // Every choice in the tray also closes it: the tray is a question, and it
  // has been answered.
  const closeThen = (fn: () => void) => () => {
    disclosure.setExpanded(false);
    fn();
  };

  const trigger = (
    <Chip
      theme={theme}
      // The outline has to be findable: this chip stands alone in a header
      // with no neighbours to imply a control, and the default border is the
      // DS-37 defect (~1.1:1 on surface).
      prominent
      // Uniform slop, unlike the tray's vertical-only: nothing sits beside
      // this one for the horizontal expansion to collide with.
      style={styles.trigger}
      // The caret states the fold rather than implying it, matching the
      // machine chip one surface over.
      label={`${current?.label ?? labels.idle} ${disclosure.expanded ? "▴" : "▾"}`}
      expanded={disclosure.expanded}
      accessibilityLabel={`${labels.a11y}${current ? `, ${current.label}` : ""}`}
      onPress={disclosure.toggle}
    />
  );

  const tray = disclosure.expanded ? (
    <ChoiceTray
      theme={theme}
      label={labels.question}
      defaultOption={{
        label: labels.fallback,
        selected: value === null,
        onChoose: closeThen(onClear),
      }}
      options={options}
      onChoose={(key) => {
        disclosure.setExpanded(false);
        onChoose(key);
      }}
      {...(onCreate
        ? { create: { label: labels.create, onPress: closeThen(onCreate) } }
        : {})}
      {...(labels.footnote ? { footnote: labels.footnote } : {})}
    />
  ) : null;

  return { trigger, tray };
}

// A ceiling, not a clip. The roster header gives the label `flex: 1` and this
// column `flexShrink: 0`, so without a cap a long place name ("Fitnessstudio"
// in German, or a hand-typed one) pushes the label to a second line or off the
// row. Chip truncates its own text at one line.
const styles = { trigger: { maxWidth: 160 } } as const;
